import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DollarSign,
  Receipt,
  Download,
  Loader2,
  Activity,
  ShoppingBag,
  Clock,
  ChevronDown,
  ChevronRight,
  Package,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";

export const CustomerSalesReportModal = ({
  open,
  onOpenChange,
  customer,
  storeId,
}) => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";
  const [downloading, setDownloading] = useState(false);
  const [expandedSaleId, setExpandedSaleId] = useState(null);

  const customerId = customer?.id;
  const customerName =
    customer?.customer_type !== "individual" && customer?.company
      ? customer.company
      : customer?.full_name ||
      `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim() ||
      "Customer";

  // 1. Fetch customerwise sales report summary
  const { data: reportData } = useQuery({
    enabled: Boolean(open && customerId),
    queryKey: ["customer-sales-report-modal", customerId, storeId],
    queryFn: async () => {
      const params = {
        customer_id: String(customerId),
      };
      if (storeId && storeId !== "all") {
        params.store_id = storeId;
      }
      const res = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.CUSTOMERWISE_SALES,
        { params },
      );
      return res.data;
    },
  });

  // 2. Fetch recent sales / purchase history for this customer
  const { data: salesData, isLoading: isLoadingSales } = useQuery({
    enabled: Boolean(open && customerId),
    queryKey: ["customer-sales-list-modal", customerId, storeId],
    queryFn: async () => {
      const params = {
        customer_id: String(customerId),
        perPage: 25,
        sort_by: "created_at",
        sort_order: "desc",
      };
      if (storeId && storeId !== "all") {
        params.store_id = storeId;
      }
      const res = await apiClient.get(API_ENDPOINTS.SALES.SALES, { params });
      return res.data;
    },
  });

  const stats = React.useMemo(() => {
    const rawList = reportData?.data || [];
    if (!Array.isArray(rawList)) return null;
    const match =
      rawList.find((r) => String(r.customer_id) === String(customerId)) ||
      rawList[0];
    return match || null;
  }, [reportData, customerId]);

  const sales = React.useMemo(() => {
    return salesData?.data || [];
  }, [salesData]);

  // Compute total spent if report hasn't finished loading yet
  const totalSpent =
    stats?.total_spent !== undefined
      ? parseFloat(stats.total_spent) || 0
      : sales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);

  const totalOrders =
    stats?.order_count !== undefined
      ? parseInt(stats.order_count) || 0
      : sales.length;

  const avgOrderValue =
    stats?.avg_order_value !== undefined
      ? parseFloat(stats.avg_order_value) || 0
      : totalOrders > 0
        ? totalSpent / totalOrders
        : 0;

  const outstanding = stats?.outstanding ? parseFloat(stats.outstanding) || 0 : 0;

  const handleDownloadPDF = async () => {
    if (!customerId) return;
    try {
      setDownloading(true);
      toast.info(`Generating sales report PDF for ${customerName}...`);
      const params = {
        customer_id: String(customerId),
      };
      if (storeId && storeId !== "all") {
        params.store_id = storeId;
      }
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.CUSTOMERWISE_SALES,
        {
          params,
          responseType: "blob",
        },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `customer-sales-report-${customerName.replace(/\s+/g, "_")}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Sales report PDF downloaded successfully!");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to download sales report PDF",
      );
    } finally {
      setDownloading(false);
    }
  };

  const getBadgeVariant = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "paid" || s === "completed") return "success";
    if (s.includes("part")) return "warning";
    if (s === "cancelled" || s === "void") return "destructive";
    return "secondary";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b bg-muted/20">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <DialogTitle className="text-xl font-bold">
                  {customerName}
                </DialogTitle>
                {customer?.customer_type && (
                  <Badge variant="outline" className="capitalize">
                    {customer.customer_type}
                  </Badge>
                )}
              </div>
              <DialogDescription className="mt-1 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                {customer?.email && <span>Email: {customer.email}</span>}
                {customer?.phone && <span>Phone: {customer.phone}</span>}
                {stats?.last_purchase_date && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last Purchase: {formatDate(stats.last_purchase_date)}
                  </span>
                )}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="rounded-lg shadow-sm"
              >
                {downloading ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-1.5 h-4 w-4" /> Download PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Body content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    Total Spent
                  </p>
                  <h3 className="text-xl font-bold text-primary mt-0.5">
                    {currencySymbol}
                    {totalSpent.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </h3>
                </div>
                <div className="p-2.5 bg-primary/10 text-primary rounded-xl">
                  <DollarSign className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="border shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    Total Orders
                  </p>
                  <h3 className="text-xl font-bold text-foreground mt-0.5">
                    {totalOrders}
                  </h3>
                </div>
                <div className="p-2.5 bg-muted text-muted-foreground rounded-xl">
                  <ShoppingBag className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="border shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    Average Order
                  </p>
                  <h3 className="text-xl font-bold text-foreground mt-0.5">
                    {currencySymbol}
                    {avgOrderValue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </h3>
                </div>
                <div className="p-2.5 bg-amber-500/10 text-amber-600 rounded-xl">
                  <Receipt className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="border shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    Outstanding
                  </p>
                  <h3
                    className={`text-xl font-bold mt-0.5 ${outstanding > 0 ? "text-destructive" : "text-foreground"
                      }`}
                  >
                    {currencySymbol}
                    {outstanding.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </h3>
                </div>
                <div className="p-2.5 bg-destructive/10 text-destructive rounded-xl">
                  <Activity className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Purchases / Invoices */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold tracking-tight text-foreground flex items-center gap-2">
                <Receipt className="h-4 w-4 text-primary" /> Purchase History &
                Invoices
              </h4>
              <span className="text-xs text-muted-foreground">
                Showing recent {sales.length} orders
              </span>
            </div>

            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="font-semibold text-xs">
                      Invoice #
                    </TableHead>
                    <TableHead className="font-semibold text-xs">
                      Date
                    </TableHead>
                    <TableHead className="font-semibold text-xs">
                      Store
                    </TableHead>
                    <TableHead className="font-semibold text-xs text-center">
                      Items
                    </TableHead>
                    <TableHead className="font-semibold text-xs text-right">
                      Total
                    </TableHead>
                    <TableHead className="font-semibold text-xs text-center">
                      Payment
                    </TableHead>
                    <TableHead className="font-semibold text-xs text-center">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingSales ? (
                    <TableRow>
                      <TableCell colSpan={8} className="h-28 text-center">
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          <span>Loading purchases...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : sales.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="h-24 text-center text-muted-foreground text-sm"
                      >
                        No purchases found for this customer.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sales.map((sale) => {
                      const isExpanded = expandedSaleId === sale.id;
                      const items = sale.items || sale.sale_items || [];
                      return (
                        <React.Fragment key={sale.id}>
                          <TableRow
                            className="cursor-pointer hover:bg-muted/30 transition-colors"
                            onClick={() =>
                              setExpandedSaleId(isExpanded ? null : sale.id)
                            }
                          >
                            <TableCell className="w-8 px-2">
                              {items.length > 0 && (
                                <button className="p-1 hover:bg-muted rounded">
                                  {isExpanded ? (
                                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                  )}
                                </button>
                              )}
                            </TableCell>
                            <TableCell className="font-medium text-xs">
                              {sale.invoice_number ||
                                sale.reference_number ||
                                `#${sale.id}`}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {formatDate(sale.sale_inv_date || sale.created_at)}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {sale.store?.name || "-"}
                            </TableCell>
                            <TableCell className="text-xs text-center">
                              <Badge variant="outline" className="text-[11px] px-1.5 py-0">
                                {sale.item_count || items.length || 1}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-right font-semibold text-foreground">
                              {currencySymbol}
                              {(parseFloat(sale.total) || 0).toLocaleString(
                                undefined,
                                { minimumFractionDigits: 2 },
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant={getBadgeVariant(sale.payment_status)}
                                className="text-[10px] capitalize px-2 py-0"
                              >
                                {sale.payment_status || "Pending"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant={
                                  sale.status === "completed"
                                    ? "success"
                                    : "secondary"
                                }
                                className="text-[10px] capitalize px-2 py-0"
                              >
                                {sale.status || "Completed"}
                              </Badge>
                            </TableCell>
                          </TableRow>

                          {/* Expanded items preview */}
                          {isExpanded && items.length > 0 && (
                            <TableRow className="bg-muted/15 hover:bg-muted/15">
                              <TableCell colSpan={8} className="p-3 pl-10">
                                <div className="space-y-1.5 text-xs">
                                  <div className="font-semibold text-muted-foreground flex items-center gap-1.5">
                                    <Package className="h-3.5 w-3.5" /> Items Purchased:
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                                    {items.map((it, idx) => (
                                      <div
                                        key={idx}
                                        className="p-2 rounded bg-background border flex justify-between items-center"
                                      >
                                        <div className="truncate pr-2">
                                          <p className="font-medium text-foreground truncate">
                                            {it.product_name ||
                                              it.product?.name ||
                                              "Product"}
                                          </p>
                                          <p className="text-[11px] text-muted-foreground">
                                            Qty: {it.quantity || 1} × {currencySymbol}
                                            {(parseFloat(it.unit_price) || 0).toFixed(2)}
                                          </p>
                                        </div>
                                        <span className="font-semibold text-xs whitespace-nowrap">
                                          {currencySymbol}
                                          {(parseFloat(it.sub_total || it.total) || 0).toFixed(2)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="p-4 border-t bg-muted/10 flex justify-between sm:justify-between items-center">
          <div className="text-xs text-muted-foreground">
            Total records: {sales.length}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadPDF}
              disabled={downloading}
            >
              <Download className="mr-1.5 h-4 w-4" /> Download Report PDF
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerSalesReportModal;
